/**
 * Starea formularului de branding, scoasă din componentă ca să fie testabilă.
 *
 * De ce contează: `PATCH /apps/:appId/branding` ÎNLOCUIEȘTE integral obiectul
 * (adminService.updateBranding - pe asta se sprijină „Reset all", care trimite
 * `{}`). Deci fiecare salvare din dashboard rescrie TOT brandingul din DB cu ce
 * știe formularul în acel moment. Orice câmp pe care formularul nu-l cunoaște -
 * fiindcă s-a montat cu date vechi, sau fiindcă planul nu-l poate edita - se
 * pierde tăcut la prima salvare a altui câmp (bug raportat 3 aug 2026: schimbi
 * culoarea, iar numele și mesajul de bun venit revin la default).
 *
 * Două reguli, ambele verificate în branding.test.ts:
 *  - `brandingForm` → `brandingPayload` e un dus-întors FĂRĂ pierderi: ce e în
 *    DB și nu e atins de om se întoarce în DB neschimbat;
 *  - un câmp golit de om chiar se golește (altfel n-ai mai putea șterge nimic).
 */
// tipurile din ./api, nu din indexul lui shared: acela trage zod în bundle (vezi main.tsx)
import type { WidgetBranding, WidgetTone, WidgetVoice } from "./api";

export const DEFAULT_PRIMARY_COLOR = "#8b5cf6";
// Textul default peste culoare: ALB, oglindind fallback-ul din widget.ts (fără branding,
// `--aig-on-primary` cade pe #fff). NU inkOnPrimary(default): în stare default SDK-ul nu
// calculează cerneala, o lasă albă. Doar pentru o culoare CUSTOM se calculează contrastul.
export const DEFAULT_TEXT_COLOR = "#ffffff";

export interface BrandingForm {
  assistantName: string;
  logoUrl: string;
  /** iconiță predefinită în buton - "" = default (marca noastră); ignorată cu un logo prezent */
  presetIcon: string;
  logoOnly: boolean;
  /** culoarea custom e opțională: fără ea, widgetul folosește defaultul din SDK (downgrade/reset safe) */
  hasCustomColor: boolean;
  primaryColor: string;
  hasTextColor: boolean;
  textColor: string;
  launcherCss: string;
  chatCss: string;
  welcomeMessage: string;
  /** mesajul de „nu am răspuns"; gol = default-ul din backend (fallbackAgent.ts) */
  fallbackMessage: string;
  tone: WidgetTone | "";
  /** opt-in la asistentul „orb" - false = panoul clasic de chat (default) */
  orbChat: boolean;
  voice: WidgetVoice;
}

/** Valorile din DB → câmpurile formularului (gol = nesetat). */
export function brandingForm(stored: WidgetBranding | null): BrandingForm {
  return {
    assistantName: stored?.assistantName ?? "",
    logoUrl: stored?.logoUrl ?? "",
    presetIcon: stored?.presetIcon ?? "",
    logoOnly: stored?.logoOnly ?? false,
    hasCustomColor: stored?.primaryColor != null,
    primaryColor: stored?.primaryColor ?? DEFAULT_PRIMARY_COLOR,
    hasTextColor: stored?.textColor != null,
    textColor: stored?.textColor ?? "",
    launcherCss: stored?.launcherCss ?? "",
    chatCss: stored?.chatCss ?? "",
    welcomeMessage: stored?.welcomeMessage ?? "",
    fallbackMessage: stored?.fallbackMessage ?? "",
    tone: stored?.tone ?? "",
    orbChat: stored?.orbChat ?? false,
    voice: stored?.voice ?? "standard",
  };
}

/**
 * Formularul → body-ul complet de PATCH.
 *
 * `stored` nu e decor: pe un plan fără CSS custom câmpurile de CSS nici măcar nu
 * se afișează, deci formularul nu le cunoaște - le trimitem înapoi exact cum
 * sunt în DB. Asta păstrează promisiunea deja scrisă în `widgetBrandingFor`
 * (appRegistry.ts): la downgrade CSS-ul RĂMÂNE în DB, doar nu se mai servește.
 * Ruta admin acceptă un CSS neschimbat fără feature-ul `customCss` tocmai
 * pentru acest ecou - a-l păstra nu înseamnă a-l folosi.
 */
export function brandingPayload(form: BrandingForm, opts: { whiteLabel: boolean; stored: WidgetBranding | null }): WidgetBranding {
  const logoUrl = form.logoUrl.trim();
  return {
    assistantName: form.assistantName.trim() || undefined,
    logoUrl: logoUrl || undefined,
    // preset + logo pot coexista în DB (logo-ul are prioritate în SDK) - îl trimitem
    // ca atare, ca dus-întorsul să rămână fără pierderi; exclusivitatea e doar UX:
    // alegerea unui preset golește logo-ul în formular, nu aici
    presetIcon: form.presetIcon || undefined,
    // „just logo" fără logo n-are sens - nu-l trimitem, la fel ca SDK-ul care-l ignoră
    logoOnly: form.logoOnly && Boolean(logoUrl) ? true : undefined,
    primaryColor: form.hasCustomColor ? form.primaryColor : undefined,
    textColor: form.hasTextColor ? form.textColor : undefined,
    launcherCss: (opts.whiteLabel ? form.launcherCss.trim() : opts.stored?.launcherCss) || undefined,
    chatCss: (opts.whiteLabel ? form.chatCss.trim() : opts.stored?.chatCss) || undefined,
    welcomeMessage: form.welcomeMessage.trim() || undefined,
    fallbackMessage: form.fallbackMessage.trim() || undefined,
    tone: form.tone || undefined,
    orbChat: form.orbChat || undefined,
    voice: form.voice !== "standard" ? form.voice : undefined,
  };
}
